import { buildQueryString } from '../../lib/query-params.ts';
import { apiClient } from '../client.ts';
import type {
  Project,
  DeployConfig,
  CreateProjectParams,
  UpdateProjectParams,
  UpdateDeployConfigParams,
  HandoffProjectConfig,
  HandoffSyncResult,
  HandoffPushResult,
  ProjectServerAssociation,
  Server,
  PaginatedResponse,
  GitTestConnectionResult,
} from '../types.ts';

export async function listProjects(params?: {
  page?: number;
  pageSize?: number;
  teamId?: string;
  /** Light field projection support (passed through; backend may ignore for now) */
  fields?: string[];
}): Promise<PaginatedResponse<Project>> {
  return apiClient.get<PaginatedResponse<Project>>(`/projects${buildQueryString(params)}`);
}

export async function getProject(projectId: string): Promise<Project> {
  return apiClient.get<Project>(`/projects/${projectId}`);
}

export async function createProject(params: CreateProjectParams): Promise<Project> {
  return apiClient.post<Project>('/projects', params);
}

export async function updateProject(
  projectId: string,
  params: UpdateProjectParams,
): Promise<Project> {
  return apiClient.patch<Project>(`/projects/${projectId}`, params);
}

export async function deleteProject(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}`);
}

// --- Server association ---

export async function listProjectServers(projectId: string): Promise<Server[]> {
  return apiClient.get<Server[]>(`/projects/${projectId}/servers`);
}

export async function associateServer(
  projectId: string,
  serverId: string,
): Promise<ProjectServerAssociation> {
  return apiClient.post<ProjectServerAssociation>(`/projects/${projectId}/servers`, { serverId });
}

export async function disassociateServer(projectId: string, serverId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/servers/${serverId}`);
}

// --- Git connection test ---

export async function testGitConnection(params: {
  repoUrl: string;
  vaultItemId: string;
}): Promise<GitTestConnectionResult> {
  return apiClient.post<GitTestConnectionResult>('/git/test-connection', params);
}

// --- Deploy config ---

export async function getDeployConfig(projectId: string): Promise<DeployConfig> {
  return apiClient.get<DeployConfig>(`/projects/${projectId}/deploy-config`);
}

export async function updateDeployConfig(
  projectId: string,
  config: UpdateDeployConfigParams,
): Promise<DeployConfig> {
  return apiClient.put<DeployConfig>(`/projects/${projectId}/deploy-config`, config);
}

// --- Handoff config ---

export async function getHandoffConfig(projectId: string): Promise<HandoffProjectConfig> {
  return apiClient.get<HandoffProjectConfig>(`/projects/${projectId}/handoff-config`);
}

export async function updateHandoffConfig(
  projectId: string,
  config: HandoffProjectConfig,
): Promise<HandoffProjectConfig> {
  return apiClient.patch<HandoffProjectConfig>(`/projects/${projectId}/handoff-config`, config);
}

export async function syncHandoffFromRepo(
  projectId: string,
  ref?: string,
): Promise<HandoffSyncResult> {
  return apiClient.post<HandoffSyncResult>(
    `/projects/${projectId}/handoff-config/sync-from-repo`,
    ref ? { ref } : {},
  );
}

export async function pushHandoffToRepo(
  projectId: string,
  params?: {
    ref?: string;
    path?: string;
    message?: string;
    handoff?: HandoffProjectConfig;
  },
): Promise<HandoffPushResult> {
  return apiClient.post<HandoffPushResult>(
    `/projects/${projectId}/handoff-config/push-to-repo`,
    params ?? {},
  );
}

/** GET /projects/:id/ai-handoff?format=json — unwrapped ProjectAIHandoff payload. */
export interface ProjectAIHandoff {
  projectId: string;
  projectName: string;
  repoUrl?: string;
  brief: string;
  recommendedPlaybook?: string | null;
  playbooks?: Record<string, unknown>;
  aiConstraints?: string[];
  apiBase?: string;
  cliHints?: Record<string, string>;
  [key: string]: unknown;
}

export async function getProjectAIHandoff(projectId: string): Promise<ProjectAIHandoff> {
  return apiClient.get<ProjectAIHandoff>(`/projects/${projectId}/ai-handoff?format=json`);
}
